import type { SendFailure } from '../../data/types';
import type {
  UploadImageBytesProgress,
  UploadProgress,
} from '../../hooks/inspectionSync/useImageSync';

/**
 * Pure core of the photo sender: the per-externalId in-flight dedupe. Kept
 * dependency-free (no Dexie / API imports) so it is unit-testable in isolation; the
 * real upload-and-finish is injected by photoDeliveryService.ts.
 */

export interface PhotoProgressListener {
  onProgress?: (progress: UploadProgress) => void;
  onByteProgress?: (bytes: UploadImageBytesProgress) => void;
}

export interface PhotoDeliveryOutcome {
  /** Photos still not received; delivery is finished only when this is empty. */
  failures: string[];
  /** Photos this run tried to upload. */
  total: number;
  /** Set when a photo failed: counted when one is missing or rejected (#11590). */
  failure?: SendFailure;
}

export type PhotoDeliveryImplementation = (
  externalId: string,
  progress: PhotoProgressListener,
) => Promise<PhotoDeliveryOutcome>;

interface RunningDelivery {
  promise: Promise<PhotoDeliveryOutcome>;
  listeners: Set<PhotoProgressListener>;
  lastProgress: UploadProgress | null;
}

/**
 * One photo sender per Inspection (#11590, SY-6): uploading an Inspection's photos and
 * finishing its delivery run once at a time, so the Submit screen and background sync
 * never both upload them. A caller arriving during a run joins it and gets its result;
 * its progress listener is told the latest progress and every update after it.
 */
export class PhotoDeliveryService {
  private inFlight = new Map<string, RunningDelivery>();

  constructor(private readonly impl: PhotoDeliveryImplementation) {}

  send(
    externalId: string,
    listener?: PhotoProgressListener,
  ): Promise<PhotoDeliveryOutcome> {
    const existing = this.inFlight.get(externalId);
    if (existing) {
      if (listener) {
        existing.listeners.add(listener);
        if (existing.lastProgress) {
          listener.onProgress?.(existing.lastProgress);
        }
      }
      return existing.promise;
    }

    const running: RunningDelivery = {
      promise: Promise.resolve({ failures: [], total: 0 }),
      listeners: new Set(listener ? [listener] : []),
      lastProgress: null,
    };
    running.promise = this.impl(externalId, {
      onProgress: (progress) => {
        running.lastProgress = progress;
        running.listeners.forEach((l) => l.onProgress?.(progress));
      },
      onByteProgress: (bytes) => {
        running.listeners.forEach((l) => l.onByteProgress?.(bytes));
      },
    }).finally(() => {
      this.inFlight.delete(externalId);
    });
    this.inFlight.set(externalId, running);
    return running.promise;
  }
}
